import React, { useEffect, useMemo, useState } from "react"
import {
  get,
  find,
  map,
  isEqual,
  orderBy,
  isEmpty,
  isNaN,
  at,
  flattenDeep,
  merge,
  filter,
  chunk,
} from "lodash"
import { Panel } from "./index"
import { Sort } from "utils/sort"
import {
  BaseTable,
  EmptyTable,
  SeparateTable,
} from "components/molecules/table"
import { filterColumns, tableColumns } from "config/table_columns"
import { Labels } from "components/atoms/input/input"
import { BaseSelect } from "components/atoms/input/select"
import { APIResponse } from "redux/apis/types"
import ClassNames from "classnames"
import {
  requestToRankingData,
  getRankingType,
  IndustryNum,
  IndustryTypes,
  IndustryTypeNums,
  Status,
  rankingList,
} from "components/actions/ranking"
import * as Formatter from "utils/Formatter"
import { statusList } from "config/const"
import { UserStorage } from "utils/userStorage"
import { useDispatch } from "react-redux"
import { changeSymbol, changeSpModalSymbol } from "redux/ducks/Chart"
import APIs from "redux/apis"
import store from "redux/store"
import { setToastData } from "redux/ducks/Toast"
import uuid from "uuid"
import { open_symbol_detail } from "utils/util"

type Option = {
  id: number | string
  name: string
  type?: string
  rankingTypeNum?: number
}

type ExchangeTypeAction = "SUMMARY_EXCHANGE" | "DETAILS_EXCHANGE"
type RankingTypeAction =
  | "SUMMARY_TYPE_1"
  | "SUMMARY_TYPE_2"
  | "SUMMARY_TYPE_3"
  | "SUMMARY_TYPE_4"

type RankingPanelType = {
  exchangeTypeAction: ExchangeTypeAction
  rankingData: APIResponse.Res
  changeRanking: Function
  typeList: Array<Option>
  exchange: number
  isPhone?: boolean
  canvasId?: string
  isForeign?: boolean
  changeDetailsData?: Function
}

type RankingTableType = {
  rankingTypeAction?: RankingTypeAction
  displayType: number
  changeRanking: Function
  rankingTypeList: Array<Option>
  rankingData: Array<object>
  requestTime: string
  rankingTypeNum: number
  exchange: number
  takeValue?: number
  isPhone?: boolean
  sortStatus?: object
  canvasId?: string
  isForeign?: boolean
  changeDetailsData?: Function
}

const labels = [{ id: 0, name: "サマリー表示" }, { id: 1, name: "詳細表示" }]

//国内株・外国株　ランキング：パネル
export const RankingPanel: React.FC<RankingPanelType> = props => {
  const { rankingData, typeList } = props
  const takeValue = rankingData.displayType === labels[0].id ? 10 : 50
  const loadResistId = useMemo(() => uuid(), [])
  const rankingSetting = props.isForeign
    ? UserStorage.ForeignRanking.get()
    : UserStorage.Ranking.get()
  const typeNameList = [
    { type: "summaryType1", exchange: get(rankingData, "summaryExchange") },
    { type: "summaryType2", exchange: get(rankingData, "summaryExchange") },
    { type: "summaryType3", exchange: get(rankingData, "summaryExchange") },
    { type: "summaryType4", exchange: get(rankingData, "summaryExchange") },
    { type: "detailsType", exchange: get(rankingData, "detailsExchange") },
  ]

  const typeNumList = map(
    typeNameList,
    (data: { type: string; exchange: number }) => {
      return {
        typeNum: get(
          typeList[get(rankingData, `${data.type}.num`)],
          "rankingTypeNum"
        ),
        exchange: data.exchange,
        isForeign: props.isForeign,
      }
    }
  )

  useEffect(() => {
    if (rankingData.displayType === labels[0].id) {
      props.changeRanking({
        actionType: "DETAILS_SORT",
        rankingData: orderBy(rankingData.detailsType.type, "Rank", "asc"),
        sortStatus: {
          status: "neutral",
          param: "",
          title: "",
        },
      })
    }
  }, [rankingData.displayType])

  return (
    <Panel
      title=""
      className={ClassNames("panel-ranking", {
        detail: rankingData.displayType === labels[1].id,
        sp: props.isPhone,
      })}
      filter={
        <>
          <div className="row-filter">
            {/*市場変更*/}
            <BaseSelect
              option={
                props.isForeign
                  ? Sort.ForeignRankingExchangeFull
                  : Sort.RankingExchangeFull
              }
              value={
                rankingData.displayType === labels[0].id
                  ? rankingData.summaryExchange
                  : rankingData.detailsExchange
              }
              onChange={(exchangeNum: number) => {
                const summaryRankingType = props.isForeign
                  ? Sort.ForeignRankingExchangeFull[exchangeNum].rankingType
                  : Sort.RankingExchangeFull[exchangeNum].rankingType
                const summaryTypeList = flattenDeep(
                  at(rankingList, summaryRankingType)
                )

                const rankingTypes = map(
                  rankingSetting,
                  (num: number, index: number) => {
                    const rankingTypeNum = get(
                      summaryTypeList[index],
                      "rankingTypeNum"
                    )
                    return (
                      getRankingType(
                        rankingTypeNum,
                        exchangeNum,
                        props.isForeign
                      ) || getRankingType(num, exchangeNum, props.isForeign)
                    )
                  }
                )
                const rankingTypeNums = map(
                  rankingSetting,
                  (num: number, index: number) => {
                    return props.isForeign && index === 3
                      ? NaN
                      : get(
                          find(summaryTypeList, (data: Option) => {
                            return props.isForeign && index === 3
                              ? NaN
                              : data.rankingTypeNum === num
                          }),
                          "id",
                          index
                        )
                  }
                )

                rankingData.displayType === labels[0].id
                  ? requestToRankingData({
                      actionType: "SUMMARY_EXCHANGE",
                      exchange: exchangeNum,
                      rankingType:
                        exchangeNum === IndustryNum && !props.isForeign
                          ? IndustryTypes
                          : rankingTypes,
                      rankingTypeNum:
                        exchangeNum === IndustryNum && !props.isForeign
                          ? IndustryTypeNums
                          : rankingTypeNums,
                      take: [takeValue, takeValue, takeValue, takeValue],
                      changeRanking: props.changeRanking,
                      loadResistId: loadResistId,
                      sortStatus: {
                        status: "neutral",
                        param: "",
                        title: "",
                      },
                    })
                  : requestToRankingData({
                      actionType: "DETAILS_EXCHANGE",
                      exchange: exchangeNum,
                      rankingType:
                        exchangeNum === IndustryNum && !props.isForeign
                          ? IndustryTypes[0]
                          : rankingTypes[0],
                      rankingTypeNum: [0],
                      take: [takeValue],
                      changeRanking: props.changeRanking,
                      loadResistId: loadResistId,
                      sortStatus: {
                        status: "neutral",
                        param: "",
                        title: "",
                      },
                    })
              }}
            />
            {!props.isPhone && (
              <Labels
                className="btn-segmented-control btn-thin liquid-control filter-control-item"
                labelList={labels}
                checked={rankingData.displayType}
                onChange={(displayNum: number) => {
                  if (rankingData.displayType === displayNum) return
                  const _rankingTypeNum =
                    displayNum === labels[0].id
                      ? map(
                          chunk(typeNumList, 4)[0],
                          (data: {
                            typeNum: number
                            exchange: number
                            isForeign: boolean
                          }) => {
                            return getRankingType(
                              data.typeNum,
                              data.exchange,
                              data.isForeign
                            )
                          }
                        )
                      : getRankingType(
                          get(typeNumList[4], "typeNum"),
                          get(typeNumList[4], "exchange"),
                          get(typeNumList[4], "isForeign")
                        )

                  requestToRankingData({
                    actionType:
                      rankingData.displayType === labels[0].id
                        ? "DISPLAY_DETAILS"
                        : "DISPLAY_SUMMARY",
                    exchange: props.exchange,
                    rankingType: _rankingTypeNum,
                    rankingTypeNum:
                      displayNum === labels[0].id
                        ? [
                            rankingData.summaryType1.num,
                            rankingData.summaryType2.num,
                            rankingData.summaryType3.num,
                            rankingData.summaryType4.num,
                          ]
                        : rankingData.detailsType.num,
                    take: displayNum === labels[0].id ? [10, 10, 10, 10] : [50],
                    changeRanking: props.changeRanking,
                    loadResistId: loadResistId,
                    sortStatus: {
                      status: "neutral",
                      param: "",
                      title: "",
                    },
                  })
                }}
              />
            )}
            {/*{sp切替 日付or設定保存ボタン}*/}
            {rankingData.displayType === labels[0].id && (
              <button
                className="btn btn-primary btn-shaped btn-save"
                onClick={() => {
                  const settingTypeNums = [
                    rankingData.summaryType1.num,
                    rankingData.summaryType2.num,
                    rankingData.summaryType3.num,
                    rankingData.summaryType4.num,
                  ]
                  const rankingTypeNums: Array<number> | {} = map(
                    settingTypeNums,
                    (num: number) => {
                      return get(typeList[num], "rankingTypeNum", NaN)
                    }
                  )
                  props.isForeign
                    ? UserStorage.ForeignRanking.set(rankingTypeNums)
                    : UserStorage.Ranking.set(rankingTypeNums)
                  store.dispatch(
                    setToastData({
                      bodyMessage: "保存しました",
                    })
                  )
                }}
              >
                設定保存
              </button>
            )}
          </div>
          {props.isPhone && (
            <div className="row-filter">
              <BaseSelect
                option={props.typeList}
                value={rankingData.detailsType.num}
                onChange={(num: number) => {
                  const rankingTypeNum = props.typeList[num].rankingTypeNum
                  const rankingType = getRankingType(
                    rankingTypeNum,
                    rankingData.detailsExchange,
                    props.isForeign
                  )
                  requestToRankingData({
                    actionType: "DETAILS_TYPE",
                    exchange: rankingData.detailsExchange,
                    rankingType: rankingType,
                    rankingTypeNum: [num],
                    take: [50],
                    changeRanking: props.changeRanking,
                    loadResistId: loadResistId,
                    sortStatus: {
                      status: "neutral",
                      param: "",
                      title: "",
                    },
                  })
                }}
              />
              <div className="time-col">
                {Formatter.date(rankingData.detailsType.time, "MM/DD HH:mm")}
              </div>
            </div>
          )}
        </>
      }
    >
      {!rankingData.displayType ? (
        <div className="split-division">
          <div className="split-container">
            {/*サマリ表示テーブル:左上*/}
            <RankingTableContent
              rankingTypeAction={"SUMMARY_TYPE_1"}
              displayType={rankingData.displayType}
              changeRanking={props.changeRanking}
              rankingTypeList={props.typeList}
              rankingData={rankingData.summaryType1.type}
              requestTime={rankingData.summaryType1.time}
              rankingTypeNum={rankingData.summaryType1.num}
              exchange={rankingData.summaryExchange}
              takeValue={takeValue}
              isForeign={props.isForeign}
            />
            {/*サマリ表示テーブル:左下*/}
            {(props.isForeign ||
              rankingData.summaryExchange !== IndustryNum) && (
              <RankingTableContent
                rankingTypeAction={"SUMMARY_TYPE_2"}
                displayType={rankingData.displayType}
                changeRanking={props.changeRanking}
                rankingTypeList={props.typeList}
                rankingData={rankingData.summaryType2.type}
                requestTime={rankingData.summaryType2.time}
                rankingTypeNum={rankingData.summaryType2.num}
                exchange={rankingData.summaryExchange}
                takeValue={takeValue}
                isForeign={props.isForeign}
              />
            )}
          </div>
          <div className="split-container">
            {/*サマリ表示テーブル:右上*/}
            {(props.isForeign ||
              rankingData.summaryExchange !== IndustryNum) && (
              <RankingTableContent
                rankingTypeAction={"SUMMARY_TYPE_3"}
                displayType={rankingData.displayType}
                changeRanking={props.changeRanking}
                rankingTypeList={props.typeList}
                rankingData={rankingData.summaryType3.type}
                requestTime={rankingData.summaryType3.time}
                rankingTypeNum={rankingData.summaryType3.num}
                exchange={rankingData.summaryExchange}
                takeValue={takeValue}
                isForeign={props.isForeign}
              />
            )}
            {/*サマリ表示テーブル:右下*/}
            {!props.isForeign && (
              <RankingTableContent
                rankingTypeAction={"SUMMARY_TYPE_4"}
                displayType={rankingData.displayType}
                changeRanking={props.changeRanking}
                rankingTypeList={props.typeList}
                rankingData={rankingData.summaryType4.type}
                requestTime={rankingData.summaryType4.time}
                rankingTypeNum={rankingData.summaryType4.num}
                exchange={rankingData.summaryExchange}
                takeValue={takeValue}
                isForeign={props.isForeign}
              />
            )}
          </div>
        </div>
      ) : (
        //詳細表示テーブル
        <RankingTableContent
          displayType={rankingData.displayType}
          changeRanking={props.changeRanking}
          rankingTypeList={props.typeList}
          rankingData={rankingData.detailsType.type}
          requestTime={rankingData.detailsType.time}
          rankingTypeNum={rankingData.detailsType.num}
          exchange={rankingData.detailsExchange}
          isPhone={props.isPhone}
          sortStatus={rankingData.sortStatus}
          canvasId={props.canvasId}
          isForeign={props.isForeign}
          changeDetailsData={props.changeDetailsData}
        />
      )}
    </Panel>
  )
}

//国内株・外国株　ランキング：テーブル
const RankingTableContent: React.FC<RankingTableType> = props => {
  const [currentPosition, changePosition] = useState<number>(0)
  const [sortTarget, changeSortTarget] = useState({ target: "", type: "" })
  const [screenSize, setScreenSize] = useState(window.outerWidth)
  const currentType = props.rankingTypeList[props.rankingTypeNum]
  const isVolumeIncrease = get(currentType, "rankingTypeNum") === 6
  const isPriceIncrease = get(currentType, "rankingTypeNum") === 7
  const _rankingTypList =
    !props.isForeign &&
    props.exchange === IndustryNum &&
    (props.rankingTypeAction === "SUMMARY_TYPE_2" ||
      props.rankingTypeAction === "SUMMARY_TYPE_3")
      ? []
      : props.rankingTypeList
  const dispatch = useDispatch()
  const loadResistId = useMemo(() => uuid(), [])

  const _selectedRankingType = get(
    find(_rankingTypList, { id: props.rankingTypeNum }),
    "type",
    "price"
  )
  const _rankingData: [] | Array<object> = map(
    props.rankingData,
    (data: object) => {
      return merge(data, {
        selectedRankingType: _selectedRankingType,
      })
    }
  )

  if (
    !props.displayType &&
    0 < _rankingData.length &&
    _rankingData.length < 10
  ) {
    for (let i = _rankingData.length; i < 10; i++) {
      _rankingData.push({})
    }
  }

  useEffect(() => {
    changeSortTarget({ target: get(currentType, "sortTarget"), type: "desc" })
  }, [props.rankingTypeNum, props.exchange])

  useEffect(() => {
    window.addEventListener("resize", () => {
      setScreenSize(window.innerWidth)
    })
  }, [])

  const _list =
    props.exchange !== IndustryNum || props.isForeign
      ? get(tableColumns.ranking.domestic, _selectedRankingType, () => {})(
          false,
          false,
          isVolumeIncrease,
          isPriceIncrease
        )
      : filter(
          get(tableColumns.ranking.domestic, _selectedRankingType, () => {})(
            false,
            false,
            isVolumeIncrease,
            isPriceIncrease
          ),
          (items, index: number) => {
            return index !== 1
          }
        )

  return props.displayType === labels[0].id ? (
    <div className="ranking-content">
      <div className="row-filter">
        <BaseSelect
          option={_rankingTypList}
          value={props.rankingTypeNum}
          //サマリ表示各カテゴリ変更
          onChange={(num: number) => {
            const _rankingTypNum = props.rankingTypeList[num].rankingTypeNum
            const _rankingType = getRankingType(
              _rankingTypNum,
              props.exchange,
              props.isForeign
            )
            requestToRankingData({
              actionType: props.rankingTypeAction,
              exchange: props.exchange,
              rankingType: _rankingType,
              rankingTypeNum: [num],
              take: [props.takeValue],
              changeRanking: props.changeRanking,
              loadResistId: loadResistId,
            })
          }}
        />
        <button
          className="btn btn-primary btn-shaped"
          onClick={() => {
            if (isNaN(props.rankingTypeNum)) {
              return
            }
            const _rankingTypNum =
              props.rankingTypeList[props.rankingTypeNum].rankingTypeNum
            const _rankingType = getRankingType(
              _rankingTypNum,
              props.exchange,
              props.isForeign
            )
            requestToRankingData({
              actionType: "DETAILS_BUTTON",
              exchange: props.exchange,
              rankingType: _rankingType,
              rankingTypeNum: [props.rankingTypeNum],
              take: [50],
              changeRanking: props.changeRanking,
              loadResistId: loadResistId,
            })
          }}
        >
          もっと見る
        </button>
        <div className="time-col">
          {Formatter.date(props.requestTime, "MM/DD HH:mm")}
        </div>
      </div>
      {isEmpty(_rankingData) ? (
        <div className="empty">
          <EmptyTable
            className="table-striped table-ranking empty"
            list={_list}
          />
        </div>
      ) : (
        <BaseTable
          className="table-list-view table-striped table-ranking summary"
          list={_list}
          data={_rankingData}
          variableTitle={
            props.exchange === IndustryNum && !props.isForeign ? "業種" : ""
          }
          onClick={(data: object) => !isEmpty(data) && open_symbol_detail(data)}
        />
      )}
    </div>
  ) : (
    <div className="ranking-content">
      {!props.isPhone && (
        <div className="row-filter">
          <BaseSelect
            option={props.rankingTypeList}
            value={props.rankingTypeNum}
            onChange={(num: number) => {
              const rankingTypeNum = props.rankingTypeList[num].rankingTypeNum
              const rankingType = getRankingType(
                rankingTypeNum,
                props.exchange,
                props.isForeign
              )
              requestToRankingData({
                actionType: "DETAILS_TYPE",
                exchange: props.exchange,
                rankingType: rankingType,
                rankingTypeNum: [num],
                take: [50],
                changeRanking: props.changeRanking,
                loadResistId: loadResistId,
              })
            }}
          />
          <div className="time-col">
            {Formatter.date(props.requestTime, "MM/DD HH:mm")}
          </div>
        </div>
      )}
      {isEmpty(_rankingData) ? (
        <div className="empty">
          <EmptyTable
            className="table-striped table-ranking empty"
            list={get(
              tableColumns.ranking.domestic,
              _selectedRankingType,
              () => {}
            )()}
          />
        </div>
      ) : (
        <div
          className={ClassNames("table-ranking-detail", { sp: props.isPhone })}
        >
          <SeparateTable
            className={ClassNames(
              "table-list-view table-striped table-ranking",
              {
                sp: props.isPhone,
              }
            )}
            list={get(
              tableColumns.ranking.domestic,
              _selectedRankingType,
              () => {}
            )(true, props.isPhone, isVolumeIncrease, isPriceIncrease)}
            scrollable={screenSize > 959 && _rankingData.length > 15}
            data={_rankingData}
            spFilter={filterColumns.ranking}
            active={currentPosition}
            variableTitle={
              props.exchange === IndustryNum && !props.isForeign ? "業種" : ""
            }
            onClick={async (data: object, i: number) => {
              changePosition(i)
              if (isEmpty(data)) return
              const symbolData = await APIs.SymbolDetail.SymbolDetail({
                SecurityType: get(data, "SecurityType"),
                Exchange: get(data, "Exchange"),
                Symbol: get(data, "Symbol") || "",
              })
              if (get(symbolData, "Result") === 0) {
                props.isPhone
                  ? dispatch(changeSpModalSymbol(get(symbolData, "Symbol")))
                  : dispatch(
                      changeSymbol(props.canvasId, get(symbolData, "Symbol"))
                    )
                props.changeDetailsData(get(symbolData, "Symbol"))
              } else if (get(symbolData, "Result") === 3) {
                store.dispatch(
                  setToastData({
                    bodyMessage: "無効な銘柄です",
                  })
                )
              }
            }}
            sortTarget={sortTarget}
            onSort={(row: object) => {
              const currentStatus = get(props, "sortStatus.status")
              const prevTarget = get(sortTarget, "target")
              const currentTarget = get(currentType, "sortTarget")
              const statusTitle = get(props, "sortStatus.title")
              const rowTitle = get(row, "title")
              const orders = getSortOrders(
                currentStatus,
                rowTitle(),
                statusTitle
              )
              const nextStatus =
                //現在ソートしているカラムと別のカラムをソートした場合ascに
                //ランキングタイプと同じカラムを切り変える場合asc <=> descの切り替えのみ
                !isEqual(rowTitle(), statusTitle) ||
                (prevTarget === currentTarget && currentStatus === "desc")
                  ? "asc"
                  : //それ以外は、asc => desc => neutral(Rankの昇順)が回る
                    get(
                      find(statusList, list => {
                        return list.currentStatus === currentStatus
                      }),
                      "nextStatus"
                    )
              const param =
                nextStatus === "neutral" ? "Rank" : get(row, "sortable")
              setSortTarget(
                currentTarget,
                prevTarget,
                param,
                nextStatus,
                changeSortTarget
              )
              props.changeRanking({
                actionType: "DETAILS_SORT",
                rankingData: orderBy(
                  _rankingData,
                  x =>
                    param === "LastClose"
                      ? get(x, "Price") - get(x, "ChangedPrice")
                      : get(x, param),
                  orders
                ),
                sortStatus: {
                  status: nextStatus,
                  param: param,
                  title: rowTitle(),
                },
              })
            }}
          />
        </div>
      )}
    </div>
  )
}

const getSortOrders = (
  currentStatus: Status,
  rowTitle: string,
  statusTitle: string
) => {
  if (!isEqual(rowTitle, statusTitle) || !statusTitle) {
    return "asc"
  } else {
    switch (currentStatus) {
      case "neutral":
        return "asc"
      case "asc":
        return "desc"
      case "desc":
        return "asc"
    }
  }
}

const setSortTarget = (
  currentTarget: string,
  prevTarget: string,
  param: string,
  nextStatus: string,
  changeSortTarget: Function
) => {
  if (prevTarget === param) {
    changeSortTarget({ target: param, type: nextStatus })
  } else if (param === "Rank") {
    changeSortTarget({ target: currentTarget, type: "desc" })
  } else {
    changeSortTarget({ target: param, type: "asc" })
  }
}
